import {useState} from 'react';
import {useParams, Link} from 'react-router-dom';
import {trpc} from '@common-web/lib/trpc';
import {DndContext, closestCenter, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import type {DragEndEvent} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import {
  ArrowLeft,
  Plus,
  GripVertical,
  Pencil,
  Trash2,
  Save,
  X,
  Clock,
  Settings,
} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@common-web/components/ui/dialog';
import {Switch} from '@common-web/components/ui/switch';
import {useAuthStore} from '@common-web/stores/authStore';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useHasPermission} from '@common-web/hooks/use-permissions';

const PHASE_OPTIONS = [
  {value: 'INITIAL_SETUP', label: 'Initial Setup', color: 'bg-brand-navy'},
  {value: 'PRE_ENROLLMENT', label: 'Pre-Enrollment', color: 'bg-brand-blue'},
  {value: 'ENROLLMENT', label: 'Enrollment', color: 'bg-brand-blue-light'},
  {value: 'GO_LIVE_PREP', label: 'Go Live Prep', color: 'bg-amber-500'},
  {value: 'POST_LAUNCH', label: 'Post Launch', color: 'bg-emerald-500'},
] as const;

type PhaseValue = (typeof PHASE_OPTIONS)[number]['value'];

function phaseLabel(phase: string): string {
  return PHASE_OPTIONS.find((p) => p.value === phase)?.label ?? phase;
}

function phaseColor(phase: string): string {
  return PHASE_OPTIONS.find((p) => p.value === phase)?.color ?? 'bg-brand-gray';
}

interface TemplateStep {
  id: string;
  stepKey: string;
  label: string;
  description: string | null;
  phaseGroup: string;
  order: number;
  slaTargetDays: number;
  isRequired: boolean;
}

interface EditingStep {
  stepId: string | null; // null = adding new
  stepKey: string;
  label: string;
  phaseGroup: PhaseValue;
  slaTargetDays: number;
  isRequired: boolean;
}

function SortableStepRow({
  step,
  editingStepInline,
  inlineLabel,
  inlineSla,
  setInlineLabel,
  setInlineSla,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onRemove,
  updatePending,
  removePending,
  canModify,
}: {
  step: TemplateStep;
  editingStepInline: string | null;
  inlineLabel: string;
  inlineSla: string;
  setInlineLabel: (v: string) => void;
  setInlineSla: (v: string) => void;
  onStartEdit: (step: TemplateStep) => void;
  onSaveEdit: (stepId: string) => void;
  onCancelEdit: () => void;
  onRemove: (stepId: string) => void;
  updatePending: boolean;
  removePending: boolean;
  /** False when the viewer lacks `onboarding.dry_run` — hides edit/delete. */
  canModify: boolean;
}) {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: step.id,
  });

  const style = {
    transform: transform
      ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)`
      : undefined,
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className='flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-50 group touch-none'
    >
      <GripVertical className='h-4 w-4 text-brand-gray-light cursor-grab' />
      <span className='text-xs text-brand-gray w-6'>{step.order}</span>

      {editingStepInline === step.id ? (
        <>
          <Input
            value={inlineLabel}
            onChange={(e) => setInlineLabel(e.target.value)}
            className='h-7 text-sm flex-1'
            autoFocus
          />
          <div className='flex items-center gap-1'>
            <Clock className='h-3 w-3 text-brand-gray' />
            <Input
              value={inlineSla}
              onChange={(e) => setInlineSla(e.target.value)}
              className='h-7 text-sm w-16'
              type='number'
            />
            <span className='text-xs text-brand-gray'>days</span>
          </div>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onSaveEdit(step.id)}
            disabled={updatePending}
          >
            <Save className='h-3.5 w-3.5 text-emerald' />
          </Button>
          <Button variant='ghost' size='icon' className='h-7 w-7' onClick={onCancelEdit}>
            <X className='h-3.5 w-3.5 text-brand-gray' />
          </Button>
        </>
      ) : (
        <>
          <span className='text-sm text-brand-navy flex-1'>{step.label}</span>
          <code className='text-xs text-brand-gray font-mono'>{step.stepKey}</code>
          <div className='flex items-center gap-1 text-xs text-brand-gray'>
            <Clock className='h-3 w-3' />
            {step.slaTargetDays}d
          </div>
          {!step.isRequired && (
            <Badge variant='outline' className='text-xs'>
              Optional
            </Badge>
          )}
          {canModify && (
            <div className='flex items-center gap-0.5 opacity-0 group-hover:opacity-100'>
              <Button
                variant='ghost'
                size='icon'
                className='h-7 w-7'
                onClick={() => onStartEdit(step)}
              >
                <Pencil className='h-3.5 w-3.5 text-brand-gray' />
              </Button>
              <Button
                variant='ghost'
                size='icon'
                className='h-7 w-7'
                onClick={() => onRemove(step.id)}
                disabled={removePending}
              >
                <Trash2 className='h-3.5 w-3.5 text-red-600' />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function OnboardingTemplateDetail() {
  const {templateId} = useParams<{templateId: string}>();
  const [editingStep, setEditingStep] = useState<EditingStep | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingStepInline, setEditingStepInline] = useState<string | null>(null);
  const [inlineSla, setInlineSla] = useState('');
  const [inlineLabel, setInlineLabel] = useState('');

  const sensors = useSensors(useSensor(PointerSensor, {activationConstraint: {distance: 8}}));

  const utils = trpc.useUtils();

  const templateQuery = trpc.internal.onboarding.getTemplate.useQuery(
    {templateId: templateId!},
    {enabled: !!templateId},
  );

  const updateStepMutation = trpc.internal.onboarding.updateTemplateStep.useMutation({
    onSuccess: () => {
      utils.internal.onboarding.getTemplate.invalidate({templateId: templateId!});
      setEditingStepInline(null);
    },
  });

  const addStepMutation = trpc.internal.onboarding.addTemplateStep.useMutation({
    onSuccess: () => {
      utils.internal.onboarding.getTemplate.invalidate({templateId: templateId!});
      setShowAddDialog(false);
      setEditingStep(null);
    },
  });

  const removeStepMutation = trpc.internal.onboarding.removeTemplateStep.useMutation({
    onSuccess: () => {
      utils.internal.onboarding.getTemplate.invalidate({templateId: templateId!});
    },
  });

  const reorderMutation = trpc.internal.onboarding.reorderTemplateSteps.useMutation({
    onSuccess: () => {
      utils.internal.onboarding.getTemplate.invalidate({templateId: templateId!});
    },
  });

  const isInternalAdmin = useHasPermission(CODE_PERMISSIONS.onboarding.dryRun.key);

  const template = templateQuery.data;

  if (!templateId) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <p className='text-brand-gray'>Template not found.</p>
      </div>
    );
  }

  if (!template) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <p className='text-brand-gray'>Loading...</p>
      </div>
    );
  }

  // Group steps by phase
  const stepsByPhase = PHASE_OPTIONS.map((phase) => ({
    ...phase,
    steps: (template.steps as TemplateStep[]).filter((s) => s.phaseGroup === phase.value),
  }));

  function handleStartInlineEdit(step: TemplateStep) {
    setEditingStepInline(step.id);
    setInlineLabel(step.label);
    setInlineSla(String(step.slaTargetDays));
  }

  function handleSaveInlineEdit(stepId: string) {
    updateStepMutation.mutate({
      stepId,
      label: inlineLabel.trim() || undefined,
      slaTargetDays: parseInt(inlineSla, 10) || undefined,
    });
  }

  function handleAddStep() {
    if (!editingStep || !template) return;
    const allSteps = template.steps as TemplateStep[];
    const maxOrder = allSteps.length > 0 ? Math.max(...allSteps.map((s) => s.order)) : 0;

    addStepMutation.mutate({
      templateId: templateId!,
      stepKey: editingStep.stepKey.trim(),
      label: editingStep.label.trim(),
      phaseGroup: editingStep.phaseGroup as 'INITIAL_SETUP',
      order: maxOrder + 1,
      slaTargetDays: editingStep.slaTargetDays,
      isRequired: editingStep.isRequired,
    });
  }

  function handleRemoveStep(stepId: string) {
    removeStepMutation.mutate({stepId});
  }

  function openAddDialog(phase?: PhaseValue) {
    setEditingStep({
      stepId: null,
      stepKey: '',
      label: '',
      phaseGroup: phase ?? 'INITIAL_SETUP',
      slaTargetDays: 7,
      isRequired: true,
    });
    setShowAddDialog(true);
  }

  function handleDragEnd(phaseSteps: TemplateStep[], event: DragEndEvent) {
    const {active, over} = event;
    if (!over || active.id === over.id) return;

    const oldIndex = phaseSteps.findIndex((s) => s.id === active.id);
    const newIndex = phaseSteps.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(phaseSteps, oldIndex, newIndex);
    reorderMutation.mutate({
      stepIds: reordered.map((s, i) => ({id: s.id, order: i + 1})),
    });
  }

  const totalSla = (template.steps as TemplateStep[]).reduce((sum, s) => sum + s.slaTargetDays, 0);

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        {/* Header */}
        <PageHeader
          title={template.name}
          description={
            template.description || `${template.steps.length} steps · ~${totalSla} days total SLA`
          }
          icon={<Settings className='h-6 w-6' />}
          isFetching={templateQuery.isFetching}
        >
          <PageHeaderAction asChild tone='secondary'>
            <Link to='/onboarding/templates'>
              <ArrowLeft className='h-4 w-4 mr-2' />
              Templates
            </Link>
          </PageHeaderAction>
          {isInternalAdmin && (
            <PageHeaderAction onClick={() => openAddDialog()}>
              <Plus className='h-4 w-4 mr-2' />
              Add Step
            </PageHeaderAction>
          )}
        </PageHeader>

        {/* Main Content */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full space-y-6'>
          {/* Phase groups */}
          {stepsByPhase.map((phase) => (
            <Card key={phase.value}>
              <CardHeader className='pb-3'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <div className={`h-3 w-3 rounded-full ${phase.color}`} />
                    <CardTitle className='text-base'>{phase.label}</CardTitle>
                    <Badge variant='outline' className='text-xs'>
                      {phase.steps.length} steps
                    </Badge>
                  </div>
                  {isInternalAdmin && (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='text-xs'
                      onClick={() => openAddDialog(phase.value)}
                    >
                      <Plus className='h-3 w-3 mr-1' />
                      Add Step
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {phase.steps.length === 0 ? (
                  <p className='text-xs text-brand-gray text-center py-4'>
                    No steps in this phase.
                  </p>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(phase.steps, event)}
                  >
                    <SortableContext
                      items={phase.steps.map((s) => s.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className='space-y-1'>
                        {phase.steps.map((step) => (
                          <SortableStepRow
                            key={step.id}
                            step={step}
                            editingStepInline={editingStepInline}
                            inlineLabel={inlineLabel}
                            inlineSla={inlineSla}
                            setInlineLabel={setInlineLabel}
                            setInlineSla={setInlineSla}
                            onStartEdit={handleStartInlineEdit}
                            onSaveEdit={handleSaveInlineEdit}
                            onCancelEdit={() => setEditingStepInline(null)}
                            onRemove={handleRemoveStep}
                            updatePending={updateStepMutation.isPending}
                            removePending={removeStepMutation.isPending}
                            canModify={isInternalAdmin}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </CardContent>
            </Card>
          ))}

          {/* Add Step Dialog */}
          <Dialog
            open={showAddDialog}
            onOpenChange={(open) => {
              setShowAddDialog(open);
              if (!open) setEditingStep(null);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Step</DialogTitle>
              </DialogHeader>
              {editingStep && (
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <Label>Step Label</Label>
                    <Input
                      value={editingStep.label}
                      onChange={(e) => setEditingStep({...editingStep, label: e.target.value})}
                      placeholder='e.g., Setup First Call with Client'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label>Step Key</Label>
                    <Input
                      value={editingStep.stepKey}
                      onChange={(e) =>
                        setEditingStep({
                          ...editingStep,
                          stepKey: e.target.value.toLowerCase().replace(/\s+/g, '_'),
                        })
                      }
                      placeholder='e.g., first_call'
                      className='font-mono text-sm'
                    />
                    <p className='text-xs text-brand-gray'>
                      Unique identifier for this step. Use snake_case.
                    </p>
                  </div>
                  <div className='space-y-2'>
                    <Label>Phase</Label>
                    <Select
                      value={editingStep.phaseGroup}
                      onValueChange={(val) =>
                        setEditingStep({...editingStep, phaseGroup: val as PhaseValue})
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PHASE_OPTIONS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label>SLA Target (days)</Label>
                      <Input
                        type='number'
                        value={editingStep.slaTargetDays}
                        onChange={(e) =>
                          setEditingStep({
                            ...editingStep,
                            slaTargetDays: parseInt(e.target.value, 10) || 0,
                          })
                        }
                        min={0}
                      />
                    </div>
                    <div className='space-y-2'>
                      <Label>Required</Label>
                      <div className='flex items-center gap-2 pt-2'>
                        <Switch
                          checked={editingStep.isRequired}
                          onCheckedChange={(checked) =>
                            setEditingStep({...editingStep, isRequired: checked})
                          }
                        />
                        <span className='text-sm text-brand-gray'>
                          {editingStep.isRequired ? 'Required' : 'Optional'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant='outline' onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAddStep}
                  disabled={
                    !editingStep?.label.trim() ||
                    !editingStep?.stepKey.trim() ||
                    addStepMutation.isPending
                  }
                >
                  {addStepMutation.isPending ? 'Adding...' : 'Add Step'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
